import { ChevronDownIcon } from "@radix-ui/react-icons";
import * as Popover from "@radix-ui/react-popover";
import "components/Dashboard/PeoplePanel/index.css";
import { ReactElement, useEffect, useRef, useState } from "react";
import { LuChevronDown } from "react-icons/lu";
import { LaneColumn, laneColumns, whenWords } from "./AutomationsTabModel";
import "./AutomationsTabTiming.css";
import { useReference } from "./hooks";
import { KindTiming, kindTiming, timingLockedWhileOn } from "./kinds";
import { entryCards, Lane, RIGHT_AWAY } from "./model";
import { Choice, ChoiceSelect, FieldRow, NumberBox } from "./parts";
import type { TimePosition, TimeUnit, Trigger } from "./types";
import { cardTimeWords } from "./words";

/*
 * A column's timing, where the lane shows it: its header is a chip with
 * the timing in words ("After 2 days", "2 days later"), and the chip opens
 * its menu: one row, a number and its unit ("[2] [day(s) later]", or
 * "Right after" alone). The first column counts from the trigger (behind
 * two or more trigger cards, from when they passed); a later one from when
 * every step in the column before it finished (after a gate, from when it
 * passed). Every step in the column shares it. A trigger card's own timing
 * ("21 days with no meeting") is a chip in the card with the same menu,
 * and Add next step's When, at the top of the step dialog, opens it too.
 */

const HOURS_MAX = 336;
const DAYS_MAX = 365;
/** How long typing in the Time row pauses before it is the timing. */
const TYPING_PAUSE_MS = 500;

/** A unit the Time row offers, with its side and limits: "[2] [day(s) later]", "[1] [day(s) before]". */
interface Amount {
  key: string;
  unit: TimeUnit;
  before: boolean;
  min: number;
  max: number;
}

/**
 * The times a column allows. The first column's count from the trigger: a
 * kind's own (its sides, unit and limits), else Right away and After N. A
 * later column's are the gap after the column before it: Right after and
 * N later.
 */
function timesOf(timing: KindTiming | null, later: boolean) {
  const fixed: { key: string; label: string }[] = [];
  const amounts: Amount[] = [];
  const amount = (unit: TimeUnit, before: boolean, min: number, max: number) =>
    max >= Math.max(1, min) &&
    amounts.push({
      key: `${before ? "before" : "after"}:${unit}`,
      unit,
      before,
      min: Math.max(1, min),
      max,
    });
  if (later || !timing) {
    fixed.push({ key: "now", label: later ? "Right after" : "Right away" });
    amount("hours", false, 1, HOURS_MAX);
    amount("days", false, 1, DAYS_MAX);
    return { fixed, amounts, nowFirst: true };
  }
  const hours = timing.key === "hours";
  // A kind in hours also takes whole days, up to its limit.
  const sideAmounts = (before: boolean, min: number, max: number) => {
    if (hours) {
      amount("hours", before, min, max);
      amount("days", before, Math.ceil(min / 24), Math.floor(max / 24));
    } else amount("days", before, min, max);
  };
  if (timing.sides === "before") sideAmounts(true, timing.min, timing.max);
  if (timing.sides === "both") {
    sideAmounts(true, 1, -timing.min);
    fixed.push({ key: "now", label: "On the day" });
    sideAmounts(false, 1, timing.max);
    return { fixed, amounts, nowFirst: false };
  }
  if (timing.sides === "after") {
    if (timing.min <= 0) fixed.push({ key: "now", label: "Right away" });
    sideAmounts(false, timing.min, timing.max);
  }
  return { fixed, amounts, nowFirst: true };
}

/** A unit as the Time row names it, the same whatever the number. */
function unitWord(unit: TimeUnit) {
  return unit === "hours" ? "hour(s)" : "day(s)";
}

/** A number within a unit's limits. */
const within = (a: Amount, n: number) => Math.min(a.max, Math.max(a.min, n));

/**
 * The Time row's number in `menu`, focused with its digits selected, so
 * typing replaces them. False when the row shows no number.
 */
function selectNumber(menu: HTMLElement | null) {
  const input = menu?.querySelector<HTMLInputElement>(
    'input[inputmode="numeric"]'
  );
  input?.focus();
  input?.select();
  return !!input;
}

/** A unit and its number as a time. */
function timeIn(a: Amount, n: number): TimePosition {
  return {
    kind: "time",
    n,
    unit: a.unit,
    ...(a.before ? { before: true } : {}),
  };
}

/**
 * The timing menu, opened by `children` (a button): the Time row, a number
 * and its unit. timing: a kind's own (its sides, unit and limits), or null
 * for a plain time; later: a gap after the column before ("Right after",
 * "N later") rather than a time from the trigger; current: the timing now.
 * wide: as wide as the field that opens it, and short enough to stay
 * inside the step dialog. onPick gets the timing: a moment at once (and
 * the menu closes), the number or unit once typing pauses, or at once on
 * Enter or closing the menu.
 */
export function TimingMenu({
  timing,
  later,
  locked = false,
  current,
  wide = false,
  label = "When",
  onPick,
  children,
}: {
  timing: KindTiming | null;
  later: boolean;
  locked?: boolean;
  current: TimePosition | null;
  wide?: boolean;
  label?: string;
  onPick: (position: TimePosition) => void;
  children: ReactElement;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [fieldWidth, setFieldWidth] = useState<number | null>(null);
  const { fixed, amounts, nowFirst } = timesOf(later ? null : timing, later);
  const amountOf = (key: string | null) =>
    amounts.find((a) => a.key === key) ?? null;
  // The Time row as the timing is now: its choice, and its number (1 when
  // it shows none).
  const rowOf = (p: TimePosition | null): { key: string | null; n: number } => {
    if (!p) return { key: null, n: 1 };
    if (p.n <= 0) return { key: fixed.length ? "now" : null, n: 1 };
    const a = amounts.find((x) => x.unit === p.unit && x.before === !!p.before);
    return a ? { key: a.key, n: within(a, p.n) } : { key: null, n: 1 };
  };
  const [row, setRow] = useState(() => rowOf(current));
  const [focusNumber, setFocusNumber] = useState(false);
  // A change in the row is the timing once typing pauses; Enter or
  // closing the menu makes it the timing at once.
  const timer = useRef<number | undefined>(undefined);
  const pending = useRef<TimePosition | null>(null);

  const cancel = () => {
    window.clearTimeout(timer.current);
    pending.current = null;
  };
  const flush = () => {
    const position = pending.current;
    cancel();
    if (position) onPick(position);
  };
  const soon = (position: TimePosition) => {
    window.clearTimeout(timer.current);
    pending.current = position;
    timer.current = window.setTimeout(flush, TYPING_PAUSE_MS);
  };
  const close = (next: boolean) => {
    if (!next) flush();
    setOpen(next);
    if (next) {
      setRow(rowOf(current));
      // The field's own width, read once as it opens: the menu's placement
      // (which runs as the dialog grows in) never sizes it.
      if (wide) setFieldWidth(triggerRef.current?.offsetWidth ?? null);
    }
  };
  /** A moment like Right after: the timing at once, and the menu closes. */
  const pick = (position: TimePosition) => {
    cancel();
    close(false);
    onPick(position);
  };

  // A unit just picked: its number, ready to type.
  useEffect(() => {
    if (!focusNumber) return;
    setFocusNumber(false);
    selectNumber(contentRef.current);
  }, [focusNumber]);

  const shown = amountOf(row.key);
  const unitChoice = (a: Amount): Choice<string> => {
    const side = later
      ? "later"
      : a.before
      ? timing?.before ?? "before"
      : timing?.after ?? "after";
    return { value: a.key, label: `${unitWord(a.unit)} ${side}` };
  };
  const moments: Choice<string>[] = fixed.map((f) => ({
    value: f.key,
    label: f.label,
  }));
  // Before the moment, then on it, then after; or right away, then later.
  const choices = nowFirst
    ? [...moments, ...amounts.map(unitChoice)]
    : [
        ...amounts.filter((a) => a.before).map(unitChoice),
        ...moments,
        ...amounts.filter((a) => !a.before).map(unitChoice),
      ];
  const chooseTime = (key: string | null) => {
    const a = amountOf(key);
    if (!a) {
      pick({ ...RIGHT_AWAY });
      return;
    }
    const n = within(a, shown ? row.n : 1);
    setRow({ key: a.key, n });
    soon(timeIn(a, n));
    setFocusNumber(true);
  };
  const changeNumber = (n: number) => {
    if (!shown) return;
    setRow({ key: shown.key, n });
    soon(timeIn(shown, n));
  };

  return (
    <Popover.Root open={open} onOpenChange={close}>
      <Popover.Trigger asChild ref={triggerRef}>
        {children}
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          ref={contentRef}
          // Over dialogs too: Add next step's When opens it in the step
          // dialog. The unit's list opens inside the menu, which grows to
          // hold it (AutomationsTabTiming.css). Wide enough for the longest
          // unit, "hour(s) before".
          className={`people-panel__menu people-panel__menu--over-dialogs flex max-w-[calc(100vw-2rem)] flex-col ${
            wide ? "max-h-[min(26rem,60vh)]" : "max-h-[70vh] w-[22rem]"
          }`}
          style={wide && fieldWidth ? { width: fieldWidth } : undefined}
          align="start"
          sideOffset={4}
          collisionPadding={12}
          aria-label={label}
          onOpenAutoFocus={(e) => {
            if (selectNumber(contentRef.current)) e.preventDefault();
          }}
        >
          <div
            className="timing-row flex items-start gap-2 px-3 py-3 text-sm"
            title={locked ? "Turn it off to change this." : undefined}
            onKeyDown={(e) => {
              const typing =
                e.target instanceof HTMLInputElement &&
                e.target.type === "number";
              if (e.key === "Enter" && typing) {
                e.preventDefault();
                close(false);
              }
              // The dropdown keeps Escape to itself: with its list shut,
              // Escape closes the menu, as it does from the number.
              if (
                e.key === "Escape" &&
                !typing &&
                !e.currentTarget.querySelector('[class*="-menu"]')
              )
                close(false);
            }}
          >
            {shown && (
              <NumberBox
                label={`How many ${shown.unit}`}
                value={row.n}
                min={shown.min}
                max={shown.max}
                disabled={locked}
                onChange={changeNumber}
              />
            )}
            <ChoiceSelect<string | null>
              label="Time"
              value={row.key}
              options={choices}
              placeholder="Pick a time"
              searchable={false}
              disabled={locked}
              className={`min-w-0 flex-1 ${wide ? "max-w-[16rem]" : ""}`}
              onChange={chooseTime}
            />
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

/** What a column's header and a card's own timing chip hold: the timing in words, and its chevron. */
function Chip({ words }: { words: string }) {
  return (
    <>
      <span className="min-w-0 text-balance break-words">{words}</span>
      <LuChevronDown
        aria-hidden
        strokeWidth={2.5}
        className="mt-1 h-3 w-3 flex-none text-gray-500"
      />
    </>
  );
}

const CHIP_LOOK =
  "inline-flex max-w-full items-start gap-1 rounded-md border border-gray-300 bg-white px-2 py-0.5 text-left text-[13px] font-semibold leading-5 text-gray-900";
const CHIP = `${CHIP_LOOK} hover:border-gray-400 data-[state=open]:border-green-500`;

/**
 * Above a trigger column, where a step column has its timing: Tadpole keeps
 * checking for the cards under it. It opens nothing.
 */
export function WatchingChip() {
  return <span className={`${CHIP_LOOK} cursor-default`}>Watching for</span>;
}

/** Before a step column's timing, so the two read as one: "Runs" "2 days later". */
export function RunsChip() {
  return <span className={`${CHIP_LOOK} flex-none cursor-default`}>Runs</span>;
}

/**
 * A column's header: its timing in words, opening the timing menu. The
 * first column counts from the trigger (a kind's own timing for one
 * trigger card, else a delay after the cards passed); a later one from the
 * column before it.
 */
export function TimingChip({
  label,
  automation,
  column,
  onPick,
}: {
  label: string;
  automation: Lane & { on: boolean };
  column: LaneColumn;
  onPick: (position: TimePosition) => void;
}) {
  const ref = useReference();
  const later = column.index > 0;
  const one = entryCards(automation).length === 1;
  const kind = automation.trigger.kind;
  return (
    <TimingMenu
      timing={!later && one ? kindTiming(ref, kind) : null}
      later={later}
      // The backend refuses changing a kind's time that keys what it sends
      // by the day while on.
      locked={automation.on && !later && one && timingLockedWhileOn(ref, kind)}
      current={column.position}
      onPick={onPick}
    >
      <button type="button" aria-label={`When: ${label}`} className={CHIP}>
        <Chip words={label} />
      </button>
    </TimingMenu>
  );
}

/**
 * A trigger card's own timing, in a stack of two or more and in a gate:
 * "21 days with no meeting", "2 hours before", opening the
 * same menu as a first column's.
 */
export function CardTimeChip({
  trigger,
  time,
  locked = false,
  onPick,
}: {
  trigger: Trigger;
  time: TimePosition;
  locked?: boolean;
  onPick: (position: TimePosition) => void;
}) {
  const ref = useReference();
  const words = cardTimeWords(ref, trigger, time);
  return (
    <TimingMenu
      timing={kindTiming(ref, trigger.kind)}
      later={false}
      locked={locked}
      current={time}
      onPick={onPick}
    >
      <button
        type="button"
        aria-label={`Its timing: ${words}`}
        className={CHIP}
        onClick={(e) => e.stopPropagation()}
      >
        <Chip words={words} />
      </button>
    </TimingMenu>
  );
}

/**
 * A timing field in the step and trigger dialogs, like the dialog's
 * selects, opening the same menu right under it: Add next step's When
 * (automation: the lane without the new step), or a new trigger card's
 * own timing (timing: its kind's).
 */
export function WhenField({
  words,
  timing,
  later,
  position,
  onPick,
}: {
  words: string;
  timing: KindTiming | null;
  later: boolean;
  position: TimePosition;
  onPick: (position: TimePosition) => void;
}) {
  return (
    <FieldRow label="When" className="">
      <TimingMenu
        timing={timing}
        later={later}
        current={position}
        wide
        onPick={onPick}
      >
        <button
          type="button"
          aria-label={`When: ${words}`}
          className="flex min-h-[2rem] w-full items-center rounded-md border border-gray-300 bg-white text-left text-sm shadow-sm hover:border-gray-400 focus:outline-none focus-visible:border-indigo-300 focus-visible:ring focus-visible:ring-indigo-200 focus-visible:ring-opacity-50 data-[state=open]:border-indigo-300 data-[state=open]:ring data-[state=open]:ring-indigo-200 data-[state=open]:ring-opacity-50"
        >
          <span className="min-w-0 flex-1 truncate pl-2.5 leading-7">
            {words}
          </span>
          <span
            aria-hidden
            className="flex flex-none items-center gap-1 self-stretch p-1"
          >
            <span className="w-px self-stretch bg-gray-300" />
            <span className="p-[3px] text-gray-500">
              <ChevronDownIcon />
            </span>
          </span>
        </button>
      </TimingMenu>
    </FieldRow>
  );
}

/** Add next step's When: the new step's timing, after the lane's last column. automation: the lane without it. */
export function NextWhenField({
  automation,
  position,
  onPick,
}: {
  automation: Lane;
  position: TimePosition;
  onPick: (position: TimePosition) => void;
}) {
  const ref = useReference();
  const columns = laneColumns(automation.steps, automation.gates ?? []);
  const one = entryCards(automation).length === 1;
  return (
    <WhenField
      words={whenWords(ref, automation, columns, position)}
      timing={
        columns.length === 0 && one
          ? kindTiming(ref, automation.trigger.kind)
          : null
      }
      later={columns.length > 0}
      position={position}
      onPick={onPick}
    />
  );
}
